import React, { useId } from 'react';
import { cn } from '@/shared/utils/cn';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type ProgressColorScheme = 'primary' | 'secondary' | 'accent';

export interface ProgressBarProps {
  /** Progress value 0 – 100 */
  value: number;
  /** Optional accessible label (used as aria-label and displayed text) */
  label?: string;
  colorScheme?: ProgressColorScheme;
  /** Show numeric percentage next to the label (default: true) */
  showPercentage?: boolean;
  className?: string;
}

// ---------------------------------------------------------------------------
// Style maps
// ---------------------------------------------------------------------------
const trackClasses: Record<ProgressColorScheme, string> = {
  primary: 'bg-primary-100',
  secondary: 'bg-secondary-100',
  accent: 'bg-accent-100',
};

const fillClasses: Record<ProgressColorScheme, string> = {
  primary: 'bg-primary-500',
  secondary: 'bg-secondary-500',
  accent: 'bg-accent-500',
};

// ---------------------------------------------------------------------------
// ProgressBar component
// ---------------------------------------------------------------------------
function ProgressBar({
  value,
  label,
  colorScheme = 'primary',
  showPercentage = true,
  className,
}: ProgressBarProps): React.ReactElement {
  const id = useId();
  // Clamp value to 0–100
  const clamped = Math.min(100, Math.max(0, value));
  const percentage = Math.round(clamped);

  return (
    <div className={cn('w-full', className)}>
      {/* Label row */}
      {(label || showPercentage) && (
        <div className="mb-1.5 flex items-center justify-between gap-2">
          {label && (
            <span id={id} className="text-sm font-medium text-slate-700 truncate">
              {label}
            </span>
          )}
          {showPercentage && (
            <span className="ml-auto shrink-0 text-xs tabular-nums text-slate-500">
              {percentage}%
            </span>
          )}
        </div>
      )}

      {/* Track */}
      <div
        role="progressbar"
        aria-valuenow={percentage}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-labelledby={label ? id : undefined}
        aria-label={!label ? `${percentage}%` : undefined}
        className={cn(
          'h-2.5 w-full overflow-hidden rounded-full',
          trackClasses[colorScheme],
        )}
      >
        {/* Fill */}
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-500 ease-out',
            fillClasses[colorScheme],
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

export default ProgressBar;
